import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { PageLoader } from '@/components/ui/Misc';
import { api, tokenStore, UNAUTHORIZED_EVENT } from './api';
import { useAccountRealtime } from './realtime';
import type { Profile, Role } from './types';

interface LoginResult extends Profile {
  token: string;
  expiresAt: string;
}

interface AuthState {
  /** null when signed out */
  profile: Profile | null;
  loading: boolean;
  login: (identifier: string, password: string, role: Role, remember: boolean) => Promise<Profile>;
  /** used after registration, which signs the new user in */
  adopt: (res: LoginResult, remember?: boolean) => void;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);
const ME = ['me'];

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const hasToken = !!tokenStore.get();

  const me = useQuery({
    queryKey: ME,
    queryFn: () => api.get<Profile>('/auth/me'),
    enabled: hasToken,
    staleTime: 30_000,
    retry: false,
  });

  // The API says the session is gone → drop to signed-out everywhere.
  useEffect(() => {
    const onGone = () => qc.setQueryData(ME, null);
    window.addEventListener(UNAUTHORIZED_EVENT, onGone);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onGone);
  }, [qc]);

  const adopt = useCallback(
    (res: LoginResult, remember = false) => {
      tokenStore.set(res.token, remember);
      qc.setQueryData<Profile>(ME, { user: res.user, merchant: res.merchant, rider: res.rider, application: res.application });
    },
    [qc],
  );

  const login = useCallback<AuthState['login']>(
    async (identifier, password, role, remember) => {
      const res = await api.post<LoginResult>('/auth/login', { identifier, password, role, remember }, { anonymous: true });
      adopt(res, remember);
      return { user: res.user, merchant: res.merchant, rider: res.rider, application: res.application };
    },
    [adopt],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      /* already expired: nothing to revoke */
    }
    tokenStore.clear();
    qc.clear();
  }, [qc]);

  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ME });
  }, [qc]);

  useAccountRealtime(
    !!me.data,
    JSON.stringify([tokenStore.get(), me.data?.user.id, me.data?.merchant?.id, me.data?.rider?.id]),
  );

  const value = useMemo<AuthState>(
    () => ({ profile: hasToken || me.data ? me.data ?? null : null, loading: hasToken && me.isLoading, login, adopt, logout, refresh }),
    [hasToken, me.data, me.isLoading, login, adopt, logout, refresh],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAuth must be used inside <AuthProvider>');
  return c;
}

export const HOME: Record<Role, string> = { merchant: '/merchant', rider: '/rider', admin: '/admin' };

/** Where a signed-in user belongs: riders whose application isn't approved yet see the status page. */
export function homeFor(p: Profile): string {
  if (p.user.role === 'rider' && !p.rider) return '/rider/application';
  return HOME[p.user.role];
}

/** Route guard: sends signed-out visitors to the login page and returns them afterwards. */
export function RequireRole({ role, children }: { role: Role; children: ReactNode }) {
  const { profile, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <PageLoader label="Signing you in…" />;
  // the admin console has its own sign-in address; merchants and riders use the public one
  if (!profile) return <Navigate to={`${role === 'admin' ? '/admin/login?' : `/login?role=${role}&`}next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (profile.user.role !== role) return <Navigate to={role === 'admin' ? '/admin/login?switch=1' : `/login?role=${role}&switch=1`} replace />;
  if (role === 'rider' && !profile.rider && !loc.pathname.startsWith('/rider/application')) return <Navigate to="/rider/application" replace />;
  return <>{children}</>;
}
